import axios, { AxiosInstance } from 'axios';
import * as Sentry from '@sentry/react';
import { config } from 'config';
import { AwsPicture, PresignedData, PresignedFields } from 'types';
import { AxiosClient, AxiosUtils } from '../AxiosClient/AxiosClient';
import { SERVICE_DOWN } from 'api/errors';

const PHOTO_RESIZE = '260x195';

class DepositPictures {
  private readonly client: AxiosInstance;

  constructor() {
    this.client = AxiosClient.get({
      baseURL: config.URL_DEPOSIT_PICTURES,
    });
  }

  async upload(pictures: File[], setLocalStorage = true, reference?: string): Promise<AwsPicture[]> {
    const nbPictures = pictures.length;
    if (nbPictures > 0) {
      const presignedList = await this.getPresignedLists(nbPictures);
      const classifiedReference = reference ?? localStorage.getItem('classifiedReference');

      if (presignedList && classifiedReference) {
        const awsPictures = await this.uploadPictures(presignedList, pictures);
        if (setLocalStorage) localStorage.setItem(`${classifiedReference}-awsPictures`, JSON.stringify(awsPictures));
        return awsPictures;
      }
    }
    return [];
  }

  async loadAWSPictures(awsPictures: AwsPicture[]): Promise<File[]> {
    const files: File[] = [];
    for (const picture of awsPictures) {
      await axios
        .get<Blob>(picture.url!, { responseType: 'blob', timeout: 5000 })
        .then(result => {
          files.push(new File([result.data], picture.key!));
        })
        .catch(r => {
          Sentry.captureException(r, {
            tags: {
              api: 'deposit-pictures-upload',
              endpoint: 'presign/deposit-pictures',
              ...AxiosUtils.enrichTags(r),
            },
          });
          return undefined;
        });
    }
    return files;
  }

  async getDepositPictures(reference: string, countValid: number, lastUpdate: string): Promise<AwsPicture[]> {
    const images = [];
    const timestamp = Date.parse(lastUpdate);
    const params = !isNaN(timestamp) ? '?version='.concat(timestamp.toString()) : '';
    for (let position = 0; position < countValid; position++) {
      images.push({
        url: `${config.URL_IMAGES_LC}${PHOTO_RESIZE}/${reference}_STANDARD_${position}.jpg${params}`,
        position,
      });
    }
    return images;
  }

  private async getPresignedLists(count: number): Promise<PresignedData[] | undefined> {
    return await this.client
      .get<PresignedData[]>(`v1/presign/deposit-pictures`, {
        params: { count },
        headers: AxiosClient.getAuthorizationHeader(),
      })
      .then(result => result.data)
      .catch(e => {
        Sentry.captureException(e, {
          tags: {
            api: 'deposit-pictures-upload',
            endpoint: 'presign/deposit-pictures',
            ...AxiosUtils.enrichTags(e),
          },
        });
        return undefined;
      });
  }

  private async uploadPictures(presignedList: PresignedData[], pictures: File[]): Promise<AwsPicture[]> {
    const awsPictures: AwsPicture[] = [];
    for (let i = 0; i < presignedList.length; i++) {
      const presigned = presignedList[i];
      const fields = presigned.fields;
      const formData = new FormData();
      if (fields) {
        Object.keys(fields).forEach(fieldName => {
          const value = fields[fieldName as keyof PresignedFields];
          if (value) {
            formData.append(fieldName, value);
          }
        });
        formData.append('file', pictures[i]);
        await AxiosUtils.addDefaultInterceptor(axios)
          .post(presigned.url, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          })
          .then(() => {
            awsPictures.push({
              key: fields.key!,
              bucket: fields.bucket!,
              url: config.URL_AWS_S3 + fields.bucket! + '/' + encodeURIComponent(fields.key!),
              action: 'INSERT',
              type: 'STANDARD',
            });
          })
          .catch(e => {
            Sentry.captureException(e, {
              tags: {
                api: 's3',
                endpoint: presigned.url,
                ...AxiosUtils.enrichTags(e),
              },
            });
            return Promise.reject({ message: SERVICE_DOWN });
          });
      }
    }
    return awsPictures;
  }
}

export const DepositPicturesService = new DepositPictures();
